// Help with a strategy: asking for it from the strategy editor (or, a setup,
// from the setup wizard), and reviewing, activating or declining the settings
// Optivo proposes in answer.
//
// Mirrors `optivo-service/src/routes/strategyHelp.ts`. Every refusal carries a
// `code`, which is what the screens react to — a proposal already decided, one
// for another property, a day's limit reached — rather than a sentence.

import { apiFetch } from "./apiClient";
import {
    activeConnectionChangedError,
    isActiveConnectionChangedRefusal,
    withPageConnection,
} from "./loadedConnection";

/** A question or a study from the editor; a setup — Optivo sets the property up — from the wizard. */
export type HelpRequestKind = "question" | "study" | "setup";

/** What the editor and the setup wizard show: where an answer goes, and what is already waiting. */
export interface StrategyHelpStatus {
    /** The address the answer goes to; null for an account with none. */
    contactEmail: string | null;
    /**
     * The people who run the property, less the caller: the colleagues "Ask
     * Optivo" offers to copy an answer to. Empty from a server that predates
     * the copies.
     */
    colleagueEmails: string[];
    /** The newest open question or study — the editor's. */
    openRequest: { id: number; kind: HelpRequestKind; createdAt: string } | null;
    /** The newest open setup request — the wizard's. */
    openSetupRequest: { id: number; createdAt: string } | null;
    pendingProposal: { id: string; createdAt: string; sentAt: string | null } | null;
    /**
     * Whether "Ask Optivo" offers the live assistant (`assistantApi.ts`) —
     * the one source for offering it. Optional, as several screens hand a
     * status of their own; absent reads as not offered.
     */
    assistantAvailable?: boolean;
}

/**
 * A refusal of a help request, with the server's reason — and the file or the
 * copied address it is about, where it is about one.
 */
export class StrategyHelpRequestError extends Error {
    readonly code: string | null;
    readonly filename: string | null;
    readonly address: string | null;

    constructor(message: string, code: string | null, filename: string | null = null, address: string | null = null) {
        super(message);
        this.name = "StrategyHelpRequestError";
        this.code = code;
        this.filename = filename;
        this.address = address;
    }
}

/** The addresses in a list the server sent, and nothing else. */
function addressList(value: unknown): string[] {
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

export async function fetchStrategyHelpStatus(): Promise<StrategyHelpStatus> {
    const res = await apiFetch("/api/strategy-help");
    if (!res.ok) throw new Error(`fetchStrategyHelpStatus failed: ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to read strategy help");
    return {
        contactEmail: data.contactEmail ?? null,
        openRequest: data.openRequest ?? null,
        openSetupRequest: data.openSetupRequest ?? null,
        pendingProposal: data.pendingProposal ?? null,
        colleagueEmails: addressList(data.colleagueEmails),
        // Only a literal true: a server that predates the assistant, or says
        // anything else, leaves the window as it was.
        assistantAvailable: data.assistantAvailable === true,
    };
}

export interface StrategyHelpRequestInput {
    kind: HelpRequestKind;
    message: string;
    /** The interface language — the one the answer is written in. */
    locale: string;
    /**
     * Screenshots and other files for support, checked first by
     * `components/support/help-attachments.ts` and again, by their bytes, by
     * the server.
     */
    attachments?: File[];
    /**
     * Colleagues to copy the answer to, checked first by
     * `components/support/help-copies.ts` and again by the server.
     */
    ccEmails?: string[];
}

export interface SentStrategyHelpRequest {
    /**
     * The request as stored — with the colleagues the server copied, which is
     * what the window may say the answer goes to: none from a server that
     * predates the copies, whatever was sent.
     */
    request: { id: number; kind: HelpRequestKind; createdAt: string; ccEmails: string[] };
    supportNotified: boolean;
    /**
     * How many of the files reached support with its note. They ride on that
     * e-mail and nowhere else, so fewer than were sent means the operator has
     * to send the rest; a server that does not say sent none.
     */
    attachmentsSent: number;
}

/**
 * A refused request or cancel: {@link ActiveConnectionChangedError} when the
 * page was loaded for another property than the one open now — which also
 * asks the shell which one is — otherwise the service's reason and code.
 */
function helpRequestRefusal(
    data: { error?: unknown; code?: unknown; filename?: unknown; address?: unknown },
    fallback: string,
): Error {
    const message = typeof data.error === "string" && data.error ? data.error : fallback;
    if (isActiveConnectionChangedRefusal(data)) return activeConnectionChangedError(message);
    return new StrategyHelpRequestError(
        message,
        typeof data.code === "string" ? data.code : null,
        typeof data.filename === "string" ? data.filename : null,
        typeof data.address === "string" ? data.address : null,
    );
}

/** A file's bytes as base64, the way `FileReader` writes them into a data URL. */
function readAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const url = typeof reader.result === "string" ? reader.result : "";
            resolve(url.slice(url.indexOf(",") + 1));
        };
        reader.onerror = () => reject(reader.error ?? new Error(`Could not read ${file.name}`));
        reader.readAsDataURL(file);
    });
}

async function encodeAttachments(
    files: readonly File[],
): Promise<Array<{ filename: string; contentType: string; contentBase64: string }>> {
    const encoded = [];
    for (const file of files) {
        try {
            encoded.push({ filename: file.name, contentType: file.type, contentBase64: await readAsBase64(file) });
        } catch {
            // Moved, deleted or revoked since it was picked: nothing is sent.
            throw new StrategyHelpRequestError(`Could not read ${file.name}`, "HELP_ATTACHMENT_UNREADABLE", file.name);
        }
    }
    return encoded;
}

/**
 * Ask for help with the open property's strategy. The request is filed for
 * the open property, which every tab shares, so it names the one the page on
 * screen was loaded for (`withPageConnection`) and is refused with
 * `ActiveConnectionChangedError` once another one is open.
 *
 * Files travel base64 in the JSON body. A body too large for whatever stands
 * in front of the service — the service's own parser, or a proxy answering
 * with a page of its own — is refused as files too large.
 */
export async function sendStrategyHelpRequest(input: StrategyHelpRequestInput): Promise<SentStrategyHelpRequest> {
    const { attachments = [], ccEmails = [], ...request } = input;
    const body = {
        ...request,
        ...(ccEmails.length > 0 ? { ccEmails } : {}),
        ...(attachments.length > 0 ? { attachments: await encodeAttachments(attachments) } : {}),
    };
    const res = await apiFetch(withPageConnection("/api/strategy-help/requests"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 413) {
        throw new StrategyHelpRequestError("The files are too large to send", "HELP_ATTACHMENTS_TOO_LARGE");
    }
    if (!res.ok || !data.success) {
        throw helpRequestRefusal(data, `sendStrategyHelpRequest failed: ${res.status}`);
    }
    return {
        request: { ...data.request, ccEmails: addressList(data.request?.ccEmails) },
        supportNotified: data.supportNotified === true,
        attachmentsSent: typeof data.attachmentsSent === "number" ? data.attachmentsSent : 0,
    };
}

/**
 * Withdraw an open request of the open property — the wizard's way out of a
 * setup request once the operator sets the property up themselves. Refused
 * with `HELP_REQUEST_NOT_FOUND` or `HELP_REQUEST_NOT_OPEN`, and, named for the
 * page's property as a request is, with `ActiveConnectionChangedError` once
 * another one is open — where it would otherwise find no such request and
 * read as one already gone.
 */
export async function cancelStrategyHelpRequest(requestId: number): Promise<void> {
    const res = await apiFetch(withPageConnection(`/api/strategy-help/requests/${requestId}/cancel`), {
        method: "POST",
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw helpRequestRefusal(data, `cancelStrategyHelpRequest failed: ${res.status}`);
    }
}

// ---------------------------------------------------------------------------
// Proposals
// ---------------------------------------------------------------------------

export type ProposalStatus = "pending" | "accepted" | "declined" | "superseded";

/**
 * What the operator asked, as its answer says it back: on the review page, in
 * the archive, and quoted under the signature of the answer's e-mail. A study
 * or a setup can be asked for with no message at all.
 */
export interface AskedQuestion {
    kind: HelpRequestKind;
    message: string;
    requesterEmail: string;
    createdAt: string;
    /**
     * A conversation with the assistant handed over without a note: the
     * operator wrote the case no word, so `message` is empty and the page says
     * what was handed over. Absent from a server that predates it, whose
     * `message` then holds the line the case stores in a note's place.
     */
    handedOver?: boolean;
    /**
     * A follow-up study the operator confirmed when activating a proposal:
     * `message` is empty, `createdAt` is that activation and `caseId` the
     * case of the proposal it follows (null where that case is gone).
     */
    followUp?: { caseId: number | null };
}

/**
 * A step a proposal is explained in: why it is taken (`why`), the outcome it
 * aims for (`aim`), the settings it moves, or, for a step the operator does
 * by hand, where they do it (`where`).
 */
export interface ProposalStep {
    title: string;
    why: string;
    aim: string;
    settings: string[];
    where?: string;
}

/**
 * Where the follow-up study offered with a proposal stands. Every field is
 * absent from a server that predates it.
 */
export interface ProposalFollowUpFields {
    /** The term support offers, in days; null offers none. */
    followUpDays?: number | null;
    /** The operator's answer at activation; null when never asked. */
    followUpConfirmed?: boolean | null;
    /** The day a confirmed follow-up falls due, YYYY-MM-DD. */
    followUpDueOn?: string | null;
    /** Its case was opened, whatever became of that case since. */
    followUpCaseOpened?: boolean;
    /** What became of that case; `gone` once it was deleted, null before it was opened. */
    followUpCaseState?: "open" | "answered" | "closed" | "gone" | null;
    /** Cancelled before it was opened. */
    followUpCancelled?: boolean;
    followUpCancelReason?: "newer_activation" | "account_deleted" | null;
}

/** One setting a proposal moves: its path under the strategy's params. */
export interface ProposalChange {
    field: string;
    from?: unknown;
    to?: unknown;
    /**
     * For a path inside a list, the list item it runs through (a special date,
     * a guest-count rung) before and after, or the whole list where its items
     * are bare values — what names the row. An item the proposal adds has no
     * `before`, one it removes no `after`. Absent from a server that predates
     * it, which the page reads around; a server that predates pairing names a
     * list item by its place, and the page pairs the items again.
     */
    item?: { path: string; before?: unknown; after?: unknown };
}

export interface StrategyProposalView {
    proposal: ProposalFollowUpFields & {
        id: string;
        status: ProposalStatus;
        connectionId: number;
        connectionName: string | null;
        /** Whether the proposal is for the property the operator has open. */
        isActiveProperty: boolean;
        summary: string;
        createdAt: string;
        sentAt: string | null;
        decidedAt: string | null;
        decidedByEmail: string | null;
        acceptedStrategyId: string | null;
        study: { filename: string } | null;
        /**
         * What the proposal's case asked; null for a study support sent
         * unasked, and absent from a server that predates it.
         */
        question?: AskedQuestion | null;
        /** Why each step is taken and what it aims for; absent from a server that predates them. */
        steps?: ProposalStep[];
    };
    /** The changes, read against what prices the property now — only while pending. */
    review: {
        /** `currency` is what money is stated in; absent from a server that predates it. */
        activeStrategy: { id: string; name: string; version: number; currency?: string | null } | null;
        changes: ProposalChange[];
        truncated: number;
        /** The proposal's own property's room types by name, whatever property is open. */
        roomKindNames?: Record<string, string>;
        /**
         * The property's own value of each setting a room type follows, keyed
         * by the path after `roomKindStrategyOverrides.<id>.`.
         */
        propertyValues?: Record<string, unknown>;
    } | null;
}

/** No proposal by that id that the caller can reach — never a failed read. */
export class ProposalNotFoundError extends Error {
    constructor() {
        super("Proposal not found");
        this.name = "ProposalNotFoundError";
    }
}

/** A proposal action refused, with the server's reason and, where decided, its status. */
export class ProposalActionError extends Error {
    readonly code: string | null;
    readonly status: ProposalStatus | null;

    constructor(message: string, code: string | null, status: ProposalStatus | null) {
        super(message);
        this.name = "ProposalActionError";
        this.code = code;
        this.status = status;
    }
}

function proposalPath(proposalId: string): string {
    return `/api/strategy-help/proposals/${encodeURIComponent(proposalId)}`;
}

export async function fetchStrategyProposal(proposalId: string): Promise<StrategyProposalView> {
    const res = await apiFetch(proposalPath(proposalId));
    if (res.status === 404) throw new ProposalNotFoundError();
    if (!res.ok) throw new Error(`fetchStrategyProposal failed: ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to read the proposal");
    return { proposal: data.proposal, review: data.review ?? null };
}

/** The study PDF's download — a plain link, so the browser keeps the session cookie. */
export function proposalStudyUrl(proposalId: string): string {
    return `${proposalPath(proposalId)}/study`;
}

async function readActionError(res: Response, fallback: string): Promise<ProposalActionError> {
    const data = await res.json().catch(() => ({}));
    return new ProposalActionError(
        data.error || `${fallback} failed: ${res.status}`,
        typeof data.code === "string" ? data.code : null,
        typeof data.status === "string" ? (data.status as ProposalStatus) : null,
    );
}

export interface AcceptedProposal {
    strategyId: string;
    version: number;
    previousStrategyId: string | null;
    previousVersion: number | null;
    /** The day the follow-up confirmed at activation falls due, YYYY-MM-DD; absent where none was. */
    followUpDueOn?: string;
}

/**
 * Activate a proposal. `followUp` answers the follow-up study it offers and
 * travels only when there is an answer; a server that predates the question
 * ignores it.
 */
export async function acceptStrategyProposal(
    proposalId: string,
    options: { followUp?: boolean } = {},
): Promise<AcceptedProposal> {
    const init: RequestInit = options.followUp === undefined
        ? { method: "POST" }
        : {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ followUp: options.followUp }),
        };
    const res = await apiFetch(`${proposalPath(proposalId)}/accept`, init);
    if (!res.ok) throw await readActionError(res, "acceptStrategyProposal");
    const data = await res.json();
    return {
        strategyId: data.strategyId,
        version: data.version,
        previousStrategyId: data.previousStrategyId ?? null,
        previousVersion: data.previousVersion ?? null,
        ...(typeof data.followUpDueOn === "string" ? { followUpDueOn: data.followUpDueOn } : {}),
    };
}

export async function declineStrategyProposal(proposalId: string): Promise<void> {
    const res = await apiFetch(`${proposalPath(proposalId)}/decline`, { method: "POST" });
    if (!res.ok) throw await readActionError(res, "declineStrategyProposal");
}

// ---------------------------------------------------------------------------
// The archive of what Optivo sent the property
// ---------------------------------------------------------------------------

/**
 * What an answer is: a study (it carries the study PDF), recommended settings
 * (settings to activate, no study), or an answer (a letter alone).
 */
export type ArchiveEntryKind = "study" | "settings" | "answer";

/** A proposal's status, or `reply` for an answer that carries no settings. */
export type ArchiveEntryStatus = ProposalStatus | "reply";

export interface ArchiveEntrySummary {
    /** The proposal's id — the address of the entry. */
    id: string;
    /** The case it answers (`OPT-<id>`). */
    caseId: number | null;
    kind: ArchiveEntryKind;
    status: ArchiveEntryStatus;
    /** The e-mail's subject, without the case the page shows beside it. */
    subject: string;
    /** The line the list shows: the summary, else what was asked, else the letter's opening. */
    excerpt: { source: "summary" | "question" | "letter"; text: string } | null;
    createdAt: string;
    sentAt: string | null;
    decidedAt: string | null;
    study: { filename: string } | null;
    figureCount: number;
}

/** A letter as its e-mail laid it out — read by the server's own parser. */
export type ArchiveLetterBlock =
    | { kind: "paragraph"; lines: string[] }
    | { kind: "figure"; filename: string; number: string | null }
    | { kind: "button" };

export interface ArchiveEntry extends ArchiveEntrySummary, ProposalFollowUpFields {
    connectionId: number;
    connectionName: string | null;
    /** Whether the answer is for the property the operator has open. */
    isActiveProperty: boolean;
    summary: string;
    letter: ArchiveLetterBlock[];
    figures: Array<{ filename: string }>;
    recipientEmail: string;
    ccEmails: string[];
    /** The language the letter is written in. */
    locale: "cs" | "en";
    decidedByEmail: string | null;
    acceptedStrategyId: string | null;
    /** What the operator asked; null for a study support sent unasked. */
    question: AskedQuestion | null;
    /** The proposal's steps; absent from a server that predates them. */
    steps?: ProposalStep[];
}

/** A question or a study asked for that has no answer yet. */
export interface WaitingHelpRequest {
    caseId: number;
    kind: HelpRequestKind;
    createdAt: string;
    /** `follow_up` for a study confirmed at an activation; absent from a server that predates it. */
    origin?: "customer" | "follow_up";
    /** For a follow-up, the day it was confirmed on the property's calendar, YYYY-MM-DD. */
    confirmedOn?: string;
}

export interface StudyArchiveView {
    entries: ArchiveEntrySummary[];
    waiting: WaitingHelpRequest[];
}

/** No answer by that id that the caller can reach — never a failed read. */
export class ArchiveEntryNotFoundError extends Error {
    constructor() {
        super("Archive entry not found");
        this.name = "ArchiveEntryNotFoundError";
    }
}

const ARCHIVE_PATH = "/api/strategy-help/archive";

/** What Optivo sent the open property, newest first, and what it is still answering. */
export async function fetchStudyArchive(): Promise<StudyArchiveView> {
    const res = await apiFetch(ARCHIVE_PATH);
    if (!res.ok) throw new Error(`fetchStudyArchive failed: ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to read the archive");
    return {
        entries: Array.isArray(data.entries) ? data.entries : [],
        waiting: Array.isArray(data.waiting) ? data.waiting : [],
    };
}

/**
 * One answer whole. A 404 is a missing answer only when the archive says so:
 * the UI and the service deploy apart, and a server that has no archive yet
 * answers 404 to everything under it — a failed read, not a missing study.
 */
export async function fetchStudyArchiveEntry(entryId: string): Promise<ArchiveEntry> {
    const res = await apiFetch(`${ARCHIVE_PATH}/${encodeURIComponent(entryId)}`);
    if (res.status === 404) {
        const data = await res.json().catch(() => ({}));
        if (data?.code === "ARCHIVE_ENTRY_NOT_FOUND") throw new ArchiveEntryNotFoundError();
    }
    if (!res.ok) throw new Error(`fetchStudyArchiveEntry failed: ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to read the archive entry");
    return data.entry;
}

/**
 * A file an answer's e-mail carried — a figure for an `<img>`, the study for a
 * download — as a plain URL, so the browser keeps the session cookie.
 */
export function studyArchiveFileUrl(entryId: string, filename: string): string {
    return `${ARCHIVE_PATH}/${encodeURIComponent(entryId)}/files/${encodeURIComponent(filename)}`;
}
